// src/pages/Announcements.tsx (Enhanced with Edit/Delete)
import React, { useState, useEffect } from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Container, 
  Grid, 
  Alert, 
  CircularProgress, 
  Box, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions,
  TextField,
  IconButton
} from '@mui/material';
import { Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { API_BASE_URL } from '../utils/api';

interface Announcement {
  id: number;
  title: string;
  message: string;
  createdAt: string;
}

const Announcements: React.FC = () => {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editDialog, setEditDialog] = useState<{ open: boolean; announcement: Announcement | null }>({ open: false, announcement: null });
  const [deleteDialog, setDeleteDialog] = useState<{ open: boolean; announcement: Announcement | null }>({ open: false, announcement: null });
  const [editData, setEditData] = useState({ title: '', message: '' });
  const [message, setMessage] = useState<{ type: 'success' | 'error', text: string } | null>(null);
  const { user } = useAuth();

  const fetchAnnouncements = async () => {
    try {
      const url = `${API_BASE_URL}/announcements`;
      console.log('[Announcements] Fetching from:', url);
      const response = await fetch(url);
      if (!response.ok) throw new Error('Failed to fetch data');
      const data = await response.json();
      setAnnouncements(data);
    } catch (err: any) {
      console.error('[Announcements] Error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const handleEdit = (announcement: Announcement) => {
    setEditData({ title: announcement.title, message: announcement.message });
    setEditDialog({ open: true, announcement });
  };

  const handleDelete = (announcement: Announcement) => {
    setDeleteDialog({ open: true, announcement });
  };

  const handleEditSubmit = async () => {
    if (!editDialog.announcement) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/announcements/${editDialog.announcement.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editData),
      });
      
      if (!response.ok) throw new Error('Failed to update announcement');
      
      setMessage({ type: 'success', text: 'Announcement updated successfully!' });
      setEditDialog({ open: false, announcement: null });
      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to update announcement' });
    }
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.announcement) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/announcements/${deleteDialog.announcement.id}`, {
        method: 'DELETE',
      });
      
      if (!response.ok) throw new Error('Failed to delete announcement');
      
      setMessage({ type: 'success', text: 'Announcement deleted successfully!' });
      setDeleteDialog({ open: false, announcement: null });
      fetchAnnouncements(); // Refresh the list
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to delete announcement' });
    }
  };

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}><CircularProgress /></Box>;
  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <Container>
      <Typography variant="h4" component="h1" gutterBottom>
        Announcements
      </Typography>
      
      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {announcements.length > 0 ? (
          announcements.map((ann) => (
            <Grid item xs={12} sm={6} md={6} key={ann.id}>
              <Card sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: 4
                }
              }}>
                <CardContent sx={{ flexGrow: 1, p: 3 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h5" component="div" sx={{ flex: 1, fontWeight: 'bold' }}>
                      {ann.title}
                    </Typography>
                    {user?.role === 'admin' && (
                      <Box>
                        <IconButton size="small" onClick={() => handleEdit(ann)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDelete(ann)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  <Typography sx={{ mb: 2, color: 'text.secondary', fontSize: '0.9rem' }}>
                    {new Date(ann.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.6 }}>
                    {ann.message}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))
        ) : (
          <Typography>No announcements found.</Typography>
        )}
      </Grid>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, announcement: null })} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Announcement</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Title"
            fullWidth
            variant="outlined"
            value={editData.title}
            onChange={(e) => setEditData({ ...editData, title: e.target.value })}
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Message"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={editData.message}
            onChange={(e) => setEditData({ ...editData, message: e.target.value })}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, announcement: null })}>Cancel</Button>
          <Button onClick={handleEditSubmit} variant="contained">Update</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, announcement: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete "{deleteDialog.announcement?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, announcement: null })}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Announcements;